import { useState } from "react";
import { useCartContext } from "../../../../customHooks/useCart";
import { persistCarePlusPlanChoice } from "../../../CarePlusShared/persistCarePlusPlanChoice";
import { trackReorderFlowEnd } from "../../ReorderTrackingManager";
import { getReorderMedicationPrice, getWlReorderPricing } from "../../reorderPrices";
import { useReorderCartSubmit } from "../ChooseTreatmentReorder/useReorderCartSubmit";
import { WL_REORDER_TEST_NAME, WlReorderVariant, isSubscriptionIntent } from "../../../../utils/wlReorderSubscriptionExperiment";

export type WlReorderOption = "subscription" | "one-time";

interface Params {
  reorderData: any;
  variant: WlReorderVariant;
  searchParams: URLSearchParams;
  country: string;
  language: string;
  setStickyNotificationDetails: any;
}

export const useWlReorderPlanSelection = ({ reorderData, variant, searchParams, country, language, setStickyNotificationDetails }: Params) => {
  const offer = reorderData.wlSubscriptionOffer;
  const { getCart, saveCart, activateSubscriptionInDB, deactivateSubscriptionInDB } = useCartContext();
  const { saveReorderToCart, goToNextStep, showSubmitError } = useReorderCartSubmit(reorderData, country, language, setStickyNotificationDetails);

  const [selectedOption, setSelectedOption] = useState<WlReorderOption>("subscription");
  const [submitting, setSubmitting] = useState(false);

  // From the B CTA: Care+ only.
  const showOneTimeOption = !isSubscriptionIntent(searchParams) && offer.allowOneTimePremium;
  const medicationPrice = getReorderMedicationPrice(reorderData);
  const pricing = getWlReorderPricing(offer, medicationPrice);
  const isSubscription = selectedOption === "subscription";
  const selectedPrice = isSubscription ? pricing.plan.headerCharged : pricing.oneTime.oneTimeChargedPrice;

  const submit = () => {
    if (submitting) return;
    trackReorderFlowEnd(reorderData.trackingContext, selectedPrice, variant, isSubscription);
    setSubmitting(true);

    // Products, then the plan via update_subscription_data (validates + attaches the bonus).
    saveReorderToCart({
      subscriptionPlanID: null,
      appliedSubscriptionDiscount: 0,
      isSftd: false,
      experiments: { [WL_REORDER_TEST_NAME]: variant },
    })
      .then(() =>
        persistCarePlusPlanChoice(
          { planID: isSubscription ? offer.subscriptionPlanID : null, singleWrite: true },
          { getCart, saveCart, activateSubscriptionInDB, deactivateSubscriptionInDB }
        )
      )
      .then(goToNextStep)
      .catch(showSubmitError)
      .finally(() => setSubmitting(false));
  };

  return {
    offer,
    pricing,
    medicationPrice,
    showOneTimeOption,
    selectedOption,
    setSelectedOption,
    submit,
    submitting,
  };
};
